import { ChatChoice, ChatChoiceAnswer } from "./chatChoice";
import { LiveChatSessionConfig } from "./client";
import { UserId } from "./common";
import { SocketType } from "./socket";
import { UserRole } from "./user";

export interface ChatSession {
    id: string;
    created: Date;
    liveChatRedirect?: Date | null;
    /** Set when the bot ends the session (no live-chat assign required). */
    endedDate?: Date | null;
    /** Why the session ended (bot-phase), e.g. `off_topic` | `abusive`. */
    endReason?: ChatSessionEndReason | string | null;
    user: ChatSessionUser;
    messages: ChatMessage[];
    assign?: ChatAssign | null;
    config?: LiveChatSessionConfig | null | undefined;
    inactivityMessagesEnabled?: boolean;
}

/** Bot-phase session end reasons from ChatWebSocketApi. */
export type ChatSessionEndReason = 'off_topic' | 'abusive' | 'inactivity' | 'bot_inactivity';

export interface SocketChatSession {
    id: string;
    created: Date;
    liveChatRedirect?: Date | null;
    /** Set when the bot ends the session (no live-chat assign required). */
    endedDate?: Date | null;
    /** Why the session ended (bot-phase), e.g. `off_topic` | `abusive`. */
    endReason?: ChatSessionEndReason | string | null;
    user: ChatSessionUser;
    messages?: ChatMessage[] | null | undefined;
    assign?: ChatAssign | null;
    clientId?: string;
    clientEmail?: string;
    config?: LiveChatSessionConfig | null | undefined;
    inactivityMessagesEnabled?: boolean;
}

export interface ChatSessionUser {
    id: UserId;
    name: string;
    surname: string;
    nickname: string | null | undefined;
    email: string;
    role: UserRole;
    external_user_id?: string | null | undefined;
}

export interface ChatMessage {
    id: string;
    sessionId: string | null | undefined;
    session?: ChatSession | null | undefined;
    content: string;
    files?: ChatFile[] | null | undefined;
    sender?: ChatSessionUser;
    created: Date;
    read?: Date | null | undefined;
    prompts?: number[];
    choiceAnswer?: ChatChoiceAnswer;
    /** Present on bot SYSTEM replies that offer customer selections (client UI only). */
    choices?: ChatChoice[];
}

export interface ChatFile {
    id: string;
    messageId: string;
    arrayBuffer?: ArrayBuffer | null | undefined;
    url?: string | null | undefined;
    /** Presigned download URL from reports / storage APIs (preferred over `url` when present). */
    signedUrl?: string | null | undefined;
    name: string;
    size: number;
    extension: string;
    category: ChatFileCategory;
    mimeType?: string;
}

export enum ChatFileCategory {
    IMAGE = 'image',
    DOCUMENT = 'document',
    TEXT = 'text',
    ARCHIVE = 'archive',
    OTHER = 'other'
}

export interface ChatAssign {
    id: string;
    user: ChatSessionUser;
    start: Date;
    end?: Date | null;
    endByUser?: ChatSessionUser | null;
    endByRole?: UserRole | null;
    closed?: Date | null;
    createdDate: Date;
}

export interface ChatProcess {
    title?: string | null | undefined;
    socketType: SocketType;
    status: ChatProcessStatus;
}
export enum ChatProcessStatus {
    NONE = 'None',
    CONFIRM_BOX = 'ConfirmBox',
    PENDING = 'Pending',
    COMPLETED = 'Completed',
    FAILED = 'Failed'
}

export enum SessionTabType {
    Active = 'Active',
    Waiting = 'Waiting'
}

export interface CloseChatSessionRequest {
    sessionId: string;
}

export interface CloseChatSessionResponse {
    success: boolean;
    session: ChatSession;
}

export interface AssignChatRequest {
    sessionId?: string;
    userId?: UserId;
}

export interface AssignChatResponse {
    success: boolean;
    session: ChatSession;
}

export interface DurationColorRule {
    start: string | null;
    end: string | null;
    color: string;
}

export interface DurationColorRuleSeconds {
    start: number | null;
    end: number | null;
    color: string;
}